import _ from 'lodash'
import { Fragment, Ref, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { useNavigate } from 'features/i18n/router'
import { Review } from 'lib/_types'
import { snowplowEvent } from 'lib/analytics'
import { ROUTE_PATH } from 'lib/paths'
import { toggleBlockUserConfirmBox } from 'store/actions/confirmBox'
import {
  useCurrentUser,
  useDeleteReviewMutation,
  usePinReviewMutation,
  useUnpinReviewMutation,
} from 'store/api/yay/user'

import ConfirmBoxBasic from '../ConfirmBoxBasic'
import LoadMore from '../LoadMore'
import Loading from '../Loading'
import { useReportModal } from '../Modal/ReportModal/ReportModal'

import { ReviewExtended, UserReviewItem } from './UserReviewItem'
import './style.scss'

export const UserReviewList = ({
  isLoading,
  hasLoadMore,
  reviews = [],
  pinnedReviews = [],
  isSelfUserReview = false,
  showReviewAsReviewer = true,
  onLoadMore,
  loadMoreRef,
  disablePinMenu,
}: {
  isLoading: boolean
  hasLoadMore: boolean
  reviews?: Array<Review>
  pinnedReviews?: Array<Review>
  isSelfUserReview?: boolean
  showReviewAsReviewer?: boolean
  onLoadMore?: () => void
  loadMoreRef?: Ref<HTMLAnchorElement>
  disablePinMenu?: boolean
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const reportModal = useReportModal()
  const [deleteReview] = useDeleteReviewMutation()
  const [pinReview] = usePinReviewMutation()
  const [unpinReview] = useUnpinReviewMutation()
  const [userWarningId, setUserWarningId] = useState(null)
  const { data: currentUser } = useCurrentUser()

  const serializedReviews = useMemo(() => {
    return _.uniqBy(
      [
        ...pinnedReviews.map((r: Review) => ({ ...r, pinned: true })),
        ...reviews,
      ],
      'id'
    ).map((review) => {
      return {
        ...review,
        deletable: isSelfUserReview || review?.reviewer?.id === currentUser?.id,
        pinnable: isSelfUserReview,
        reviewer: showReviewAsReviewer ? review?.reviewer : review?.user,
      } as ReviewExtended
    })
  }, [
    reviews,
    pinnedReviews,
    currentUser,
    isSelfUserReview,
    showReviewAsReviewer,
  ])

  const getMenu = (review: ReviewExtended) => {
    const menu = []

    if (review.deletable) {
      menu.push({
        title: t('common.delete'),
        onClick: () => {
          deleteReview(review.id).then((res) => {
            if (res?.['data']?.result == 'success') {
              snowplowEvent.delete_letter({
                letter: {
                  sender_id: isSelfUserReview
                    ? review?.reviewer?.id
                    : currentUser?.id,
                  receiver_id: review?.user?.id,
                },
              })

              window.location.reload()
            }
          })
        },
      })
    }

    if (review?.reviewer?.id !== currentUser?.id) {
      menu.push(
        {
          title: t('common.block'),
          onClick: () =>
            dispatch(
              toggleBlockUserConfirmBox({ user: { id: review?.reviewer?.id } })
            ),
        },
        {
          title: t('common.report'),
          onClick: () =>
            reportModal.open({ userId: review?.reviewer?.id?.toString?.() }),
        }
      )
    }

    if (!disablePinMenu && review.pinnable) {
      if (review.pinned) {
        menu.push({
          title: t('common.unpin'),
          onClick: () => {
            unpinReview(review.id).then((res) => {
              if (res?.['data']?.result == 'success') {
                window.location.reload()
              }
            })
          },
        })
      } else {
        menu.push({
          title: t('common.pin'),
          onClick: () => {
            pinReview(review.id).then((res) => {
              if (res?.['data']?.result == 'success') {
                window.location.reload()
              }
            })
          },
        })
      }
    }

    return menu
  }

  return (
    <Fragment>
      {isLoading && reviews.length === 0 && <Loading />}

      {!hasLoadMore && !isLoading && reviews.length === 0 && (
        <div className="Box__warning">{t('common.no_letters')}</div>
      )}

      {serializedReviews.length > 0 && (
        <dl className="UserReview">
          {serializedReviews.map((review, index) => {
            return (
              <UserReviewItem
                key={review.id}
                index={index}
                review={review}
                menu={getMenu(review)}
                setUserWarningId={setUserWarningId}
                isSelfUserReview={isSelfUserReview}
              />
            )
          })}

          {hasLoadMore && (
            <LoadMore
              className="UserReview__item UserReview__item--load-more"
              onClick={onLoadMore}
              isLoading={isLoading}
              ref={loadMoreRef}
            />
          )}
        </dl>
      )}

      <ConfirmBoxBasic
        title={t('user_review_list.confirm.title')}
        confirmButtonText={t('common.see_profile')}
        cancelText={t('common.cancel')}
        onOk={() => navigate(ROUTE_PATH.USER(userWarningId))}
        onCancel={() => setUserWarningId(null)}
        onClose={() => setUserWarningId(null)}
        isActive={userWarningId !== null}
      />
    </Fragment>
  )
}
